import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';
import { STATUS_TONE_CLASSES, statusHeadline } from '@/utils/flightStatusDisplay';

/**
 * A flight's live status as a pill on the Trips list ("● In the air",
 * "● Delayed 45 min"), sitting just left of the trip's own status badge and
 * sized like it. The flight number and time go in the card's reference line
 * (FlightListItem.jsx, cardStatusText). Rendered only for a flight with live
 * status (src/hooks/useFlightLive.js).
 */
export default function FlightStatusChip({ segment, className }) {
  const headline = statusHeadline(segment?.status);
  if (!headline) return null;
  const tone = STATUS_TONE_CLASSES[headline.tone] || STATUS_TONE_CLASSES.muted;
  const short = shortStatusLabel(headline.label);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap rounded-full border px-2 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-xs font-normal',
        tone.pill,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 flex-shrink-0 rounded-full', tone.dot)} aria-hidden="true" />
      {short === headline.label ? headline.label : (
        <>
          <span className="sm:hidden">{short}</span>
          <span className="hidden sm:inline">{headline.label}</span>
        </>
      )}
    </span>
  );
}

/**
 * The pill's words on a phone: just the state ("Delayed", "In the air"). A
 * long "Delayed 1 hr 20 min" widened the pill column and cut off the time on
 * the line beside it; the new time already shows there, and the trip page
 * has the details.
 */
function shortStatusLabel(label) {
  const state = String(label || '').split(' · ')[0];
  return /^Delayed\b/.test(state) ? 'Delayed' : state;
}

FlightStatusChip.propTypes = {
  segment: PropTypes.shape({ status: PropTypes.object }),
  className: PropTypes.string,
};
